import { CircularProgress, Button } from '@mui/material';
import React from 'react';
import { LoadingStatus, useLoadingStore } from '../../store/LoadingStore';
import { Visualization } from '../../rendering/Visualizer';
import NotificationProvider from './generic/Notification';
import ConfirmationProvider from './generic/ConfirmationProvider';
import LoadingScreen from './LoadingScreen';
import DesignSchemes from './panels/DesignSchemes';
import Data from './panels/data/Data';
import Element from './panels/Element';
import LeftSettings from './panels/settings/LeftSettings';
import Column from './generic/Column';
import RightSettings from './panels/settings/RightSettings';

// Edit mode
import { useUIMode, UIMode, ModeReason } from '../store/useUIMode';

// Legend
import { OverlayLegend } from './panels/Overlays/OverlayLegend';

// Notification
import useNotificationStore from '../store/notification';
import { NotificationType } from '@visualizer/types';
import ProjectSpecifications from './panels/ProjectSpecifications';
import PerformanceMetrics from './panels/PerformanceMetrics';
import ErrorScreen from './ErrorScreen';
import FiltersPresetsTabs from './panels/Filter/FiltersPresetsTabs';
import OverlayCompass from './panels/Overlays/OverlayCompass';
import OverlayPovSensorWarning from './panels/Overlays/OverlayPovSensorWarning';
import { useSessionStore } from '../store/session/uiSessionStore';
import GuidedTour from './GuidedTour';
import { useCompactUI } from './compactUi';
import { getSentence } from '../constants/sentences';

interface GUIProps {
    viewer: Visualization;
}

const LEFT_PANEL_DEFAULT_RATIOS = {
    data: 0.48,
    element: 0.3,
    project: 0.22,
} as const;

const RIGHT_PANEL_DEFAULT_RATIOS = {
    filter: 0.48,
    design: 0.3,
    metrics: 0.22,
} as const;

type LeftPanelKey = keyof typeof LEFT_PANEL_DEFAULT_RATIOS;
type RightPanelKey = keyof typeof RIGHT_PANEL_DEFAULT_RATIOS;

const LEFT_PANEL_KEYS = Object.keys(LEFT_PANEL_DEFAULT_RATIOS) as LeftPanelKey[];
const RIGHT_PANEL_KEYS = Object.keys(RIGHT_PANEL_DEFAULT_RATIOS) as RightPanelKey[];

const createToggleHandlers = <T extends string>(toggleFn: (panel: T) => void, panels: ReadonlyArray<T>): Record<T, () => void> =>
    panels.reduce(
        (acc, panel) => {
            acc[panel] = () => toggleFn(panel);
            return acc;
        },
        {} as Record<T, () => void>
    );

const computePanelRatios = <T extends string>(expanded: Record<T, boolean>, defaults: Record<T, number>): Record<T, number> => {
    const keys = Object.keys(expanded) as T[];
    const zeroed = keys.reduce(
        (acc, key) => {
            acc[key] = 0;
            return acc;
        },
        {} as Record<T, number>
    );

    const expandedKeys = keys.filter((key) => expanded[key]);
    const expandedCount = expandedKeys.length;

    if (expandedCount === 0) return zeroed;

    if (expandedCount === 1) {
        const [onlyKey] = expandedKeys;
        return { ...zeroed, [onlyKey]: 1 };
    }

    if (expandedCount === 2) {
        return keys.reduce(
            (acc, key) => {
                acc[key] = expanded[key] ? 0.5 : 0;
                return acc;
            },
            {} as Record<T, number>
        );
    }

    const total = keys.reduce((sum, key) => sum + defaults[key], 0);
    return keys.reduce(
        (acc, key) => {
            acc[key] = defaults[key] / total;
            return acc;
        },
        {} as Record<T, number>
    );
};

const computePanelStyles = <T extends string>(ratios: Record<T, number>, expanded: Record<T, boolean>): Record<T, React.CSSProperties> => {
    const keys = Object.keys(ratios) as T[];
    return keys.reduce(
        (acc, key) => {
            if (expanded[key]) {
                const ratio = ratios[key];
                acc[key] = {
                    flex: `${ratio} 1 0%`,
                    height: `${(ratio * 100).toFixed(2)}%`,
                };
            } else {
                acc[key] = {
                    flex: '0 0 auto',
                    height: 'auto',
                };
            }
            return acc;
        },
        {} as Record<T, React.CSSProperties>
    );
};

const formatRecordingElapsed = (elapsedMs: number) => {
    const totalSeconds = Math.max(0, Math.floor(elapsedMs / 1000));
    const minutes = Math.floor(totalSeconds / 60)
        .toString()
        .padStart(2, '0');
    const seconds = (totalSeconds % 60).toString().padStart(2, '0');
    return `${minutes}:${seconds}`;
};

const COMPACT_UI_WARNING_MESSAGE = getSentence('CompactUI.Warning.Message');

const GUI: React.FC<GUIProps> = (props) => {
    const loadingStatus = useLoadingStore((state) => state.loadingStatus);
    const loadingMessage = useSessionStore((s) => s.loadingMessage);
    const freeScreenRecording = useLoadingStore((s) => s.freeScreenRecording);
    const isCompactUI = useCompactUI();

    // Edit mode
    const { mode, reason } = useUIMode();

    // Notifier
    const showNotification = useNotificationStore((s) => s.showNotification);
    const dismissNotification = useNotificationStore((s) => s.dismissNotification);
    const prevModeRef = React.useRef<UIMode | null>(null);
    const prevCompactUIRef = React.useRef(false);
    const recordingTitleRef = React.useRef<string | null>(null);

    const [collapsed, setCollapsed] = React.useState<{ left: boolean; right: boolean }>({
        left: false,
        right: false,
    });
    const [leftPanelsExpanded, setLeftPanelsExpanded] = React.useState<Record<LeftPanelKey, boolean>>({
        data: true,
        element: true,
        project: true,
    });
    const [rightPanelsExpanded, setRightPanelsExpanded] = React.useState<Record<RightPanelKey, boolean>>({
        filter: true,
        design: true,
        metrics: true,
    });
    const leftPanelRatios = React.useMemo(() => computePanelRatios(leftPanelsExpanded, LEFT_PANEL_DEFAULT_RATIOS), [leftPanelsExpanded]);
    const rightPanelRatios = React.useMemo(
        () => computePanelRatios(rightPanelsExpanded, RIGHT_PANEL_DEFAULT_RATIOS),
        [rightPanelsExpanded]
    );

    const leftPanelStyles = React.useMemo(
        () => computePanelStyles(leftPanelRatios, leftPanelsExpanded),
        [leftPanelRatios, leftPanelsExpanded]
    );
    const rightPanelStyles = React.useMemo(
        () => computePanelStyles(rightPanelRatios, rightPanelsExpanded),
        [rightPanelRatios, rightPanelsExpanded]
    );

    const toggleLeftPanel = React.useCallback((panel: LeftPanelKey) => {
        setLeftPanelsExpanded((current) => {
            if (current[panel]) {
                const currentlyExpanded = Object.values(current).filter(Boolean).length;
                if (currentlyExpanded <= 1) {
                    return current;
                }
            }
            return { ...current, [panel]: !current[panel] };
        });
    }, []);

    const toggleRightPanel = React.useCallback((panel: RightPanelKey) => {
        setRightPanelsExpanded((current) => {
            if (current[panel]) {
                const currentlyExpanded = Object.values(current).filter(Boolean).length;
                if (currentlyExpanded <= 1) {
                    return current;
                }
            }
            return { ...current, [panel]: !current[panel] };
        });
    }, []);

    const leftPanelToggleHandlers = React.useMemo(() => createToggleHandlers(toggleLeftPanel, LEFT_PANEL_KEYS), [toggleLeftPanel]);
    const rightPanelToggleHandlers = React.useMemo(() => createToggleHandlers(toggleRightPanel, RIGHT_PANEL_KEYS), [toggleRightPanel]);

    React.useEffect(() => {
        // Evita notifiche prima che il visualizer sia pronto
        if (loadingStatus !== LoadingStatus.VISUALIZER_READY) return;

        const prev = prevModeRef.current;
        if (prev !== mode) {
            if (mode === UIMode.EditGeodata) {
                showNotification(
                    `Edit Geodata is active${reason !== ModeReason.None ? ` (${reason})` : ''}. Add a Simulation Set and at least one Layer to enable analysis panels.`,
                    NotificationType.Warning
                );
            } else if (mode === UIMode.SiteAnalysis && prev === UIMode.EditGeodata) {
                showNotification('Site Analysis mode enabled.', NotificationType.Success);
            }
            prevModeRef.current = mode;
        }
    }, [mode, reason, loadingStatus, showNotification]);

    React.useEffect(() => {
        if (loadingStatus !== LoadingStatus.VISUALIZER_READY) {
            return;
        }

        if (isCompactUI && !prevCompactUIRef.current) {
            showNotification(COMPACT_UI_WARNING_MESSAGE, NotificationType.Warning, true, true);
        }

        if (!isCompactUI && prevCompactUIRef.current) {
            useNotificationStore
                .getState()
                .notifications.filter((notification) => notification.message === COMPACT_UI_WARNING_MESSAGE)
                .forEach((notification) => dismissNotification(notification.id));
        }

        prevCompactUIRef.current = isCompactUI;
    }, [dismissNotification, isCompactUI, loadingStatus, showNotification]);

    React.useEffect(() => {
        if (!freeScreenRecording?.active) {
            if (recordingTitleRef.current != null) {
                document.title = recordingTitleRef.current;
                recordingTitleRef.current = null;
            }
            return;
        }

        if (recordingTitleRef.current == null) {
            recordingTitleRef.current = document.title;
        }

        const updateTitle = () => {
            document.title = `REC ${formatRecordingElapsed(performance.now() - freeScreenRecording.startedAtMs)} | ${recordingTitleRef.current}`;
        };

        updateTitle();

        const intervalId = window.setInterval(updateTitle, 1000);

        return () => {
            window.clearInterval(intervalId);
            if (recordingTitleRef.current != null) {
                document.title = recordingTitleRef.current;
                recordingTitleRef.current = null;
            }
        };
    }, [freeScreenRecording?.active, freeScreenRecording?.startedAtMs]);

    React.useEffect(() => {
        const { body, documentElement } = document;
        const prevLeftDrawerVisible = documentElement.style.getPropertyValue('--left-drawer-visible');
        const prevRightDrawerVisible = documentElement.style.getPropertyValue('--right-drawer-visible');

        if (isCompactUI) {
            body.dataset.compactUi = '1';
            documentElement.style.setProperty('--left-drawer-visible', '0px');
            documentElement.style.setProperty('--right-drawer-visible', '0px');
        } else {
            delete body.dataset.compactUi;
        }

        return () => {
            delete body.dataset.compactUi;
            if (prevLeftDrawerVisible) {
                documentElement.style.setProperty('--left-drawer-visible', prevLeftDrawerVisible);
            } else {
                documentElement.style.removeProperty('--left-drawer-visible');
            }

            if (prevRightDrawerVisible) {
                documentElement.style.setProperty('--right-drawer-visible', prevRightDrawerVisible);
            } else {
                documentElement.style.removeProperty('--right-drawer-visible');
            }
        };
    }, [isCompactUI]);

    const drawColumns = () => (
        <>
            <Column
                alignment="left"
                isCollapsed={collapsed.left}
                onToggle={() => setCollapsed((s) => ({ ...s, left: !s.left }))}
                header={<LeftSettings />}
            >
                <Data
                    isCollapsed={!leftPanelsExpanded.data}
                    onToggleCollapse={leftPanelToggleHandlers.data}
                    panelsExpanded={leftPanelsExpanded}
                    style={leftPanelStyles.data}
                />
                <Element
                    isCollapsed={!leftPanelsExpanded.element}
                    onToggleCollapse={leftPanelToggleHandlers.element}
                    panelsExpanded={leftPanelsExpanded}
                    style={leftPanelStyles.element}
                />
                <ProjectSpecifications
                    isCollapsed={!leftPanelsExpanded.project}
                    onToggleCollapse={leftPanelToggleHandlers.project}
                    panelsExpanded={leftPanelsExpanded}
                    style={leftPanelStyles.project}
                />
            </Column>

            <Column
                alignment="right"
                isCollapsed={collapsed.right}
                onToggle={() => setCollapsed((s) => ({ ...s, right: !s.right }))}
                header={<RightSettings viewer={props.viewer} />}
            >
                <FiltersPresetsTabs
                    isCollapsed={!rightPanelsExpanded.filter}
                    onToggleCollapse={rightPanelToggleHandlers.filter}
                    panelsExpanded={rightPanelsExpanded}
                    style={rightPanelStyles.filter}
                />
                <DesignSchemes
                    isCollapsed={!rightPanelsExpanded.design}
                    onToggleCollapse={rightPanelToggleHandlers.design}
                    panelsExpanded={rightPanelsExpanded}
                    style={rightPanelStyles.design}
                />
                <PerformanceMetrics
                    isCollapsed={!rightPanelsExpanded.metrics}
                    onToggleCollapse={rightPanelToggleHandlers.metrics}
                    panelsExpanded={rightPanelsExpanded}
                    style={rightPanelStyles.metrics}
                />
            </Column>

            <NotificationProvider />
            <ConfirmationProvider />
        </>
    );

    let mainContent: React.ReactNode = null;
    switch (loadingStatus) {
        case LoadingStatus.INITIALIZING:
            mainContent = (
                <CircularProgress
                    size={100}
                    style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                    }}
                />
            );
            break;

        case LoadingStatus.LOADING:
            mainContent = <LoadingScreen />;
            break;

        case LoadingStatus.LOADED:
            mainContent = <LoadingScreen drawingStuffs={true} />;
            break;

        case LoadingStatus.VISUALIZER_READY:
            mainContent = (
                <>
                    {drawColumns()}
                    <OverlayLegend corner="br" />
                    <OverlayCompass viewer={props.viewer} />
                    <OverlayPovSensorWarning corner="tr" />
                    <GuidedTour />
                </>
            );
            break;

        case LoadingStatus.ERROR:
            mainContent = <ErrorScreen />;
            break;

        default:
            mainContent = null;
    }

    return (
        <>
            {mainContent}
            {loadingMessage ? (
                <div className="loading-overlay" role="status" aria-live="polite">
                    <div className="loading-overlay-card">
                        <CircularProgress size={36} thickness={4} />
                        <div className="loading-overlay-text">
                            <p className="loading-overlay-title">{loadingMessage.title}</p>
                            {loadingMessage.description ? (
                                <p className="loading-overlay-description">{loadingMessage.description}</p>
                            ) : null}

                            {/* PROGRESS BAR */}
                            {typeof loadingMessage.progress === 'number' ? (
                                <div style={{ marginTop: 12, width: '100%' }}>
                                    <div style={{ fontSize: 12, opacity: 0.8, marginBottom: 6 }}>{loadingMessage.progress}%</div>
                                    <div style={{ height: 6, borderRadius: 999, background: 'rgba(255,255,255,0.15)' }}>
                                        <div
                                            style={{
                                                height: 6,
                                                borderRadius: 999,
                                                width: `${Math.max(0, Math.min(100, loadingMessage.progress))}%`,
                                                background: 'rgba(255,255,255,0.7)',
                                            }}
                                        />
                                    </div>
                                </div>
                            ) : null}

                            {/* CANCEL BUTTON */}
                            {loadingMessage.cancellable && loadingMessage.onCancel ? (
                                <div style={{ marginTop: 12, display: 'flex', justifyContent: 'flex-end' }}>
                                    <Button variant="outlined" size="small" onClick={() => loadingMessage.onCancel?.()}>
                                        Cancel
                                    </Button>
                                </div>
                            ) : null}
                        </div>
                    </div>
                </div>
            ) : null}
        </>
    );
};

export default GUI;
